import "./App.css";
import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import FactoryIcon from "@mui/icons-material/Factory";
import HealingIcon from "@mui/icons-material/Healing";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import SellIcon from "@mui/icons-material/Sell";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import { fadeInUp } from "@ntg/component-library/utilities";
import { motion } from "motion/react";
import { useLayoutContext } from "@ntg/component-library/contexts";
import { useNavigate } from "react-router";

interface IHeroCard {
    description: string;
    icon: React.ReactNode;
    title: string;
    to: string | null;
}

const HERO_CARDS: IHeroCard[] = [
    {
        description:
            "Search Sales Orders, review header and line details, and confirm or update them in D365 Finance & Operations.",
        icon: <SellIcon fontSize="large" />,
        title: "Sales Orders",
        to: "/salesorder",
    },
    {
        description:
            "Find Service Orders, inspect their lines, and walk them through the same D365 commit pipeline as Sales Orders.",
        icon: <HealingIcon fontSize="large" />,
        title: "Service Orders",
        to: "/serviceorder",
    },
    {
        description:
            "Browse Manufacturing items and run the cascading commit chain that releases Products, BOMs, Routes and Sales Order Lines into D365.",
        icon: <FactoryIcon fontSize="large" />,
        title: "Manufacturing",
        to: "/manufacturing",
    },
    {
        description:
            "Track active and recent Function App orchestrations as they sync data from CS into D365.",
        icon: <MonitorHeartIcon fontSize="large" />,
        title: "Process Monitor",
        to: "/monitor",
    },
    {
        description:
            "Visualise how Sales Orders, Service Orders, and Manufacturing Items connect to their downstream Routes, BOMs, and Released Products. Open from any order or item toolbar.",
        icon: <AccountTreeIcon fontSize="large" />,
        title: "Data Explorer",
        to: null,
    },
    {
        description:
            "Run ad-hoc, read-only OData queries against any D365 Finance & Operations data entity. Build typed filters, browse paged results, share queries by URL.",
        icon: <TravelExploreIcon fontSize="large" />,
        title: "OData Explorer",
        to: "/odata-explorer",
    },
];

export default function Home() {
    const { SetPrimaryTitle, SetSecondaryHeader } = useLayoutContext();
    const navigate = useNavigate();

    useEffect(() => {
        SetPrimaryTitle("HOME");
        SetSecondaryHeader(null);
        return () => {
            SetPrimaryTitle("");
            SetSecondaryHeader(null);
        };
    }, [SetPrimaryTitle, SetSecondaryHeader]);

    return (
        <motion.div {...fadeInUp}>
            <Box sx={{ p: 3 }}>
                <Stack spacing={1} sx={{ mb: 4 }}>
                    <Typography variant="h4" fontWeight={600}>
                        D365 Finance &amp; Operations
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Bridge between CS and Dynamics 365 F&amp;O. Search orders, monitor the sync pipeline, and explore how records connect across the two systems.
                    </Typography>
                </Stack>

                <Box
                    sx={{
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                            lg: "repeat(3, 1fr)",
                        },
                    }}
                >
                    {HERO_CARDS.map((card) => {
                        const inner = (
                            <CardContent>
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <Box sx={{ color: "primary.main", mt: 0.5 }}>
                                        {card.icon}
                                    </Box>
                                    <Stack spacing={0.5}>
                                        <Typography variant="h6" fontWeight={600}>
                                            {card.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {card.description}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        );
                        return (
                            <Card key={card.title} variant="outlined">
                                {card.to ? (
                                    <CardActionArea
                                        onClick={() => navigate(card.to!)}
                                        sx={{ height: "100%" }}
                                    >
                                        {inner}
                                    </CardActionArea>
                                ) : (
                                    inner
                                )}
                            </Card>
                        );
                    })}
                </Box>
            </Box>
        </motion.div>
    );
}
